import React, { useState, useEffect } from "react";
import * as adminService from "@/shared/api/services/adminService";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { createPageUrl } from "@/shared/utils";
import { format } from "date-fns";
import { toast } from "sonner";
import { useLanguage } from "@/shared/components/shared/LanguageContext";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/shared/components/ui/alert-dialog";
import { Plus, Search, MoreHorizontal, Pencil, Trash2, Shield, Lock, Loader2 } from "lucide-react";

const C = {
  bg: "#060C18", card: "#0E1C35", cardHigh: "#122040", cardHover: "#162B4D",
  border: "rgba(255,255,255,0.07)", borderHover: "rgba(34,211,238,0.35)",
  cyan: "#22D3EE", cyanSoft: "rgba(34,211,238,0.08)",
  purple: "#A78BFA", purpleSoft: "rgba(167,139,250,0.08)",
  blue: "#3B82F6", rose: "#FB7185",
  t1: "#EFF6FF", t2: "#93B4D4", t3: "#4D6E90", t4: "#2A4060",
};

const DARK_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800&display=swap');
.roles-dark,.roles-dark *{font-family:'Outfit',system-ui,sans-serif !important;box-sizing:border-box}

/* ─── SEARCH BAR FIX – proper icon positioning, no overlap ─── */
.roles-search-wrap{
  position:relative;display:flex;align-items:center;
  max-width:400px;width:100%;
}
.roles-search-icon{
  position:absolute;left:12px;top:50%;transform:translateY(-50%);
  width:15px;height:15px;color:#4D6E90;
  pointer-events:none;z-index:2;flex-shrink:0;display:block;
}
.roles-search-input{
  display:block !important;width:100% !important;height:40px !important;
  padding:0 14px 0 40px !important;
  background:#0E1C35 !important;
  border:1px solid rgba(255,255,255,0.09) !important;
  color:#EFF6FF !important;border-radius:10px !important;
  font-size:13px !important;font-weight:500 !important;
  font-family:'Outfit',system-ui,sans-serif !important;
  outline:none !important;transition:all .2s !important;
}
.roles-search-input::placeholder{color:#4D6E90 !important}
.roles-search-input:hover{
  border-color:rgba(34,211,238,0.35) !important;
  background:#162B4D !important;
}
.roles-search-input:focus{
  border-color:rgba(34,211,238,0.6) !important;
  box-shadow:0 0 0 3px rgba(34,211,238,0.13) !important;
  background:#122040 !important;
}

/* dropdown */
[data-radix-dropdown-menu-content]{background:#0D1B2E !important;border:1px solid rgba(34,211,238,0.18) !important;border-radius:12px !important;box-shadow:0 16px 48px rgba(0,0,0,0.7) !important;padding:4px !important}
[data-radix-dropdown-menu-content] [role="menuitem"]{color:#93B4D4 !important;border-radius:8px !important;font-size:13px !important;padding:8px 12px !important;transition:background .15s,color .15s !important}
[data-radix-dropdown-menu-content] [role="menuitem"]:hover{background:rgba(34,211,238,0.10) !important;color:#22D3EE !important}

/* alert dialog */
[role="alertdialog"]{background:#0A1628 !important;border:1px solid rgba(255,255,255,0.1) !important;border-radius:16px !important}
[role="alertdialog"] h2{color:#EFF6FF !important}
[role="alertdialog"] p{color:#93B4D4 !important}

/* scrollbar */
::-webkit-scrollbar{width:6px;height:6px}
::-webkit-scrollbar-track{background:rgba(14,28,53,0.3);border-radius:10px}
::-webkit-scrollbar-thumb{background:rgba(34,211,238,0.2);border-radius:10px}
::-webkit-scrollbar-thumb:hover{background:rgba(34,211,238,0.35)}
`;

export default function AdminRoles() {
  const [search, setSearch] = useState("");
  const [deleteDialog, setDeleteDialog] = useState(null);
  const { t } = useLanguage();
  const queryClient = useQueryClient();

  useEffect(() => {
    const id = "roles-dark-css";
    if (document.getElementById(id)) return;
    const el = document.createElement("style");
    el.id = id; el.textContent = DARK_CSS;
    document.head.appendChild(el);
    return () => document.getElementById(id)?.remove();
  }, []);

  const translateRoleDescription = (description) => {
    if (!description) return "";
    const key = `roleDesc.${description}`;
    const translated = t(key);
    return translated !== key ? translated : description;
  };

  const { data: roles = [], isLoading } = useQuery({
    queryKey: ["available-roles"],
    queryFn: () => adminService.getAvailableRoles(),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => adminService.deleteStaffRole(id),
    onSuccess: () => {
      queryClient.invalidateQueries(["available-roles"]);
      setDeleteDialog(null);
      toast.success(t("roleDeletedSuccessfully") || "Role deleted successfully");
    },
    onError: (err) => {
      toast.error(err?.message || "Failed to delete role");
    }
  });

  const filteredRoles = roles.filter(r =>
    r.name?.toLowerCase().includes(search.toLowerCase()) ||
    r.description?.toLowerCase().includes(search.toLowerCase())
  );
  const systemRoles = filteredRoles.filter(r => r.isSystem);
  const customRoles = filteredRoles.filter(r => !r.isSystem);

  /* Shared table renderer */
  const RoleTable = ({ roleList, isSystem }) => (
    <div style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr style={{ borderBottom: `1px solid ${C.border}` }}>
            {[t("role"), t("permissions"), t("created"), t("actions")].map((h, i) => (
              <th key={h} style={{ padding: "16px 24px", textAlign: i === 3 ? "right" : "left", fontSize: 11, fontWeight: 700, color: C.t3, textTransform: "uppercase", letterSpacing: "0.1em" }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {roleList.map(role => (
            <tr key={role.id} style={{ borderBottom: `1px solid ${C.border}`, transition: "background .2s" }}
              onMouseEnter={e => e.currentTarget.style.background = isSystem ? "rgba(167,139,250,0.03)" : "rgba(34,211,238,0.03)"}
              onMouseLeave={e => e.currentTarget.style.background = "transparent"}
            >
              {/* Name */}
              <td style={{ padding: "20px 24px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <div style={{ width: 44, height: 44, borderRadius: 12, background: isSystem ? C.purpleSoft : C.cyanSoft, border: `1px solid ${isSystem ? "rgba(167,139,250,0.2)" : "rgba(34,211,238,0.2)"}`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    {isSystem ? <Lock size={20} style={{ color: C.purple }} /> : <Shield size={20} style={{ color: C.cyan }} />}
                  </div>
                  <div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <p style={{ fontSize: 14, fontWeight: 600, color: C.t1, margin: 0 }}>{role.name}</p>
                      {isSystem && <span style={{ display: "inline-flex", padding: "2px 8px", borderRadius: 6, fontSize: 10, fontWeight: 700, background: C.purpleSoft, border: "1px solid rgba(167,139,250,0.22)", color: C.purple, textTransform: "uppercase", letterSpacing: "0.05em" }}>{t("systemBadge")}</span>}
                    </div>
                    {role.description && <p style={{ fontSize: 12, color: C.t3, margin: "2px 0 0" }}>{translateRoleDescription(role.description)}</p>}
                  </div>
                </div>
              </td>
              {/* Permissions */}
              <td style={{ padding: "20px 24px" }}>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6, maxWidth: 400 }}>
                  {role.permissions?.length > 0 ? (
                    <>
                      {role.permissions.slice(0, 3).map((perm, idx) => (
                        <span key={idx} style={{ display: "inline-flex", padding: "4px 10px", borderRadius: 8, fontSize: 11, fontWeight: 600, background: isSystem ? "rgba(167,139,250,0.08)" : "rgba(59,130,246,0.08)", border: `1px solid ${isSystem ? "rgba(167,139,250,0.2)" : "rgba(59,130,246,0.2)"}`, color: isSystem ? C.purple : C.blue }}>{perm}</span>
                      ))}
                      {role.permissions.length > 3 && <span style={{ display: "inline-flex", padding: "4px 10px", borderRadius: 8, fontSize: 11, fontWeight: 600, background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", color: C.t3 }}>+{role.permissions.length - 3} {t("morePermissions")}</span>}
                    </>
                  ) : (
                    <span style={{ fontSize: 12, color: C.t4 }}>{t("noPermissions")}</span>
                  )}
                </div>
              </td>
              {/* Created */}
              <td style={{ padding: "20px 24px", fontSize: 13, color: C.t3, fontWeight: 500 }}>
                {format(new Date(role.createdAt), "MMM d, yyyy")}
              </td>
              {/* Actions */}
              <td style={{ padding: "20px 24px", textAlign: "right" }}>
                {isSystem ? (
                  <button disabled style={{ width: 36, height: 36, display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 10, cursor: "not-allowed", background: C.card, border: `1px solid ${C.border}`, color: C.t4, opacity: 0.5, marginLeft: "auto" }}>
                    <MoreHorizontal size={16} />
                  </button>
                ) : (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button style={{ width: 36, height: 36, display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 10, cursor: "pointer", background: C.card, border: `1px solid ${C.border}`, color: C.t2, transition: "all .2s", marginLeft: "auto" }}
                        onMouseEnter={e => { e.currentTarget.style.background = C.cardHover; e.currentTarget.style.borderColor = C.borderHover; e.currentTarget.style.color = C.cyan }}
                        onMouseLeave={e => { e.currentTarget.style.background = C.card; e.currentTarget.style.borderColor = C.border; e.currentTarget.style.color = C.t2 }}
                      ><MoreHorizontal size={16} /></button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <Link to={createPageUrl("AdminRoleForm") + `?id=${role.id}`}>
                        <DropdownMenuItem><Pencil className="h-4 w-4 mr-2" />{t("edit")}</DropdownMenuItem>
                      </Link>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => setDeleteDialog(role)} className="text-red-600">
                        <Trash2 className="h-4 w-4 mr-2" />{t("delete")}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="roles-dark" style={{ minHeight: "100vh", background: C.bg }}>
      {/* BG */}
      <div aria-hidden style={{ position: "fixed", inset: 0, pointerEvents: "none", backgroundImage: "linear-gradient(rgba(34,211,238,0.02) 1px,transparent 1px),linear-gradient(90deg,rgba(34,211,238,0.02) 1px,transparent 1px)", backgroundSize: "60px 60px", maskImage: "radial-gradient(ellipse 80% 80% at 50% 30%,black 20%,transparent 100%)" }} />
      <div aria-hidden style={{ position: "fixed", inset: 0, pointerEvents: "none", background: "radial-gradient(ellipse 1000px 600px at 5% 5%,rgba(34,211,238,0.04) 0%,transparent 70%),radial-gradient(ellipse 800px 500px at 95% 85%,rgba(167,139,250,0.04) 0%,transparent 65%)" }} />

      <div className="max-w-[1440px] mx-auto md:p-8 flex flex-col gap-6 relative z-10">

        {/* ── Header ── */}
        <div style={{ display: "flex", flexDirection: "column", gap: 20, marginBottom: 32 }}>
          <div className="flex flex-col justify-between gap-4 sm:flex-row">
            <div style={{ display: "flex", alignItems: "flex-start", gap: 16 }}>
              <div style={{ width: 60, height: 60, borderRadius: 18, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, background: "linear-gradient(135deg,rgba(34,211,238,0.1),rgba(167,139,250,0.1))", border: "1px solid rgba(34,211,238,0.22)", boxShadow: "0 8px 24px rgba(34,211,238,0.1)" }}>
                <Shield size={26} style={{ color: C.cyan }} />
              </div>
              <div>
                <h1 style={{ fontSize: 30, fontWeight: 800, color: C.t1, margin: 0, letterSpacing: "-0.6px" }}>{t("staffRoles")}</h1>
                <p style={{ fontSize: 13, color: C.t3, margin: "6px 0 0", fontWeight: 500 }}>
                  {roles.length} {t("rolesCount")} · <span style={{ color: C.cyan }}>{customRoles.length} {t("customCount")}</span> · <span style={{ color: C.purple }}>{systemRoles.length} {t("systemCount")}</span>
                </p>
              </div>
            </div>
            <Link to={createPageUrl("AdminRoleForm")} style={{ textDecoration: "none" }}>
              <button style={{ width: "100%", display: "inline-flex", alignItems: "center", gap: 8, padding: "0 20px", height: 44, borderRadius: 11, fontSize: 13, fontWeight: 700, cursor: "pointer", border: "1px solid rgba(34,211,238,0.3)", background: "linear-gradient(135deg,rgba(34,211,238,0.12),rgba(59,130,246,0.12))", color: C.cyan, transition: "all .2s", boxShadow: "0 4px 16px rgba(34,211,238,0.15)" }}
                onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 8px 24px rgba(34,211,238,0.25)" }}
                onMouseLeave={e => { e.currentTarget.style.transform = ""; e.currentTarget.style.boxShadow = "0 4px 16px rgba(34,211,238,0.15)" }}
              ><Plus size={16} />{t("createRole")}</button>
            </Link>
          </div>

          {/* ── FIXED SEARCH BAR ── */}
          <div className="roles-search-wrap">
            <Search className="roles-search-icon" />
            <input className="roles-search-input" type="text"
              placeholder={t("searchRolesPlaceholder")}
              value={search} onChange={e => setSearch(e.target.value)} />
          </div>
        </div>

        {isLoading ? (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "80px 0" }}>
            <Loader2 className="animate-spin" size={40} style={{ color: C.cyan, marginBottom: 16 }} />
            <p style={{ color: C.t3, fontSize: 14 }}>{t("loadingRoles") || "Loading roles..."}</p>
          </div>
        ) : (
          <>
            {/* Custom Roles */}
            {customRoles.length > 0 && (
              <div style={{ marginBottom: 24, background: `linear-gradient(155deg,${C.cardHigh},${C.card})`, border: `1px solid ${C.border}`, borderRadius: 20, overflow: "hidden", boxShadow: "0 4px 24px rgba(0,0,0,0.2)" }}>
                <div style={{ position: "relative", padding: "20px 24px", borderBottom: `1px solid ${C.border}` }}>
                  <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 1, background: "linear-gradient(90deg,transparent,rgba(34,211,238,0.15),transparent)" }} />
                  <h2 style={{ fontSize: 16, fontWeight: 700, color: C.t1, margin: 0 }}>{t("customRoles")}</h2>
                  <p style={{ fontSize: 12, color: C.t3, margin: "4px 0 0" }}>{t("rolesYouveCreated")}</p>
                </div>
                <RoleTable roleList={customRoles} isSystem={false} />
              </div>
            )}

            {/* System Roles */}
            {systemRoles.length > 0 && (
              <div style={{ background: `linear-gradient(155deg,${C.cardHigh},${C.card})`, border: `1px solid ${C.border}`, borderRadius: 20, overflow: "hidden", boxShadow: "0 4px 24px rgba(0,0,0,0.2)" }}>
                <div style={{ position: "relative", padding: "20px 24px", borderBottom: `1px solid ${C.border}` }}>
                  <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 1, background: "linear-gradient(90deg,transparent,rgba(167,139,250,0.15),transparent)" }} />
                  <h2 style={{ fontSize: 16, fontWeight: 700, color: C.t1, margin: 0 }}>{t("systemRoles")}</h2>
                  <p style={{ fontSize: 12, color: C.t3, margin: "4px 0 0" }}>{t("predefinedRolesReadOnly")}</p>
                </div>
                <RoleTable roleList={systemRoles} isSystem={true} />
              </div>
            )}

            {/* Empty state */}
            {filteredRoles.length === 0 && (
              <div style={{ background: `linear-gradient(155deg,${C.cardHigh},${C.card})`, border: `1px solid ${C.border}`, borderRadius: 20, padding: 60, textAlign: "center", boxShadow: "0 4px 24px rgba(0,0,0,0.2)" }}>
                <div style={{ width: 64, height: 64, borderRadius: 16, background: C.cyanSoft, border: "1px solid rgba(34,211,238,0.2)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px" }}><Shield size={28} style={{ color: C.cyan }} /></div>
                <h3 style={{ fontSize: 18, fontWeight: 700, color: C.t1, margin: "0 0 8px" }}>{t("noRolesFound")}</h3>
                <p style={{ fontSize: 13, color: C.t3, margin: "0 0 24px" }}>{search ? t("tryDifferentSearchTerm") : t("getStartedCreatingFirstRole")}</p>
                {!search && (
                  <Link to={createPageUrl("AdminRoleForm")} style={{ textDecoration: "none" }}>
                    <button style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "0 20px", height: 44, borderRadius: 11, fontSize: 13, fontWeight: 700, cursor: "pointer", border: "1px solid rgba(34,211,238,0.3)", background: "linear-gradient(135deg,rgba(34,211,238,0.12),rgba(59,130,246,0.12))", color: C.cyan, transition: "all .2s" }}
                      onMouseEnter={e => e.currentTarget.style.transform = "translateY(-2px)"}
                      onMouseLeave={e => e.currentTarget.style.transform = ""}
                    ><Plus size={16} />{t("createRole")}</button>
                  </Link>
                )}
              </div>
            )}
          </>
        )}

        {/* Delete Dialog */}
        <AlertDialog open={!!deleteDialog} onOpenChange={() => setDeleteDialog(null)}>
          <AlertDialogContent style={{ background: "#0A1628", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 16 }}>
            <AlertDialogHeader>
              <AlertDialogTitle style={{ color: "#EFF6FF" }}>{t("deleteRole")}</AlertDialogTitle>
              <AlertDialogDescription style={{ color: "#93B4D4" }}>
                {t("deleteRoleConfirmation").replace("{name}", deleteDialog?.name || "")}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={deleteMutation.isLoading || deleteMutation.isPending} style={{ background: "transparent", border: "1px solid rgba(255,255,255,0.12)", color: "#93B4D4", borderRadius: 10 }}>{t("cancel")}</AlertDialogCancel>
              <AlertDialogAction
                disabled={deleteMutation.isLoading || deleteMutation.isPending}
                onClick={(e) => {
                  e.preventDefault(); // Prevent dialog from closing immediately
                  if (deleteDialog?.isSystem) { toast.error(t("systemRolesCannotBeDeleted")); setDeleteDialog(null); }
                  else deleteMutation.mutate(deleteDialog.id);
                }}
                style={{ background: "linear-gradient(135deg,#FB7185,#e55c6c)", border: "none", borderRadius: 10, color: "#fff", fontWeight: 700, display: "flex", alignItems: "center", gap: 8 }}
              >
                {(deleteMutation.isLoading || deleteMutation.isPending) && <Loader2 className="animate-spin" size={16} />}
                {t("delete")}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}